import { FC, useState } from 'react';
import {
    Box,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { MoreVert, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import TimeHelper from '../../../../utils/time';
import { SearchServiceCommand } from '../../../../services/command';
import { enumOrderStatus } from '../../../../types/enums/status';
import { COIP, coipCls } from './customerOrderInformationPaper.classes';
import { formatOrderDeliveredDate } from './customerOrderInformationPaper.helpers';

const other_link: string = import.meta.env.VITE_APPURL!;

export const extractProductIdsFromShippingList = (shippingListItems?: any[][]): number[] => {
    if (!shippingListItems) {
        return [];
    }
    return shippingListItems.flatMap((itemArray) => itemArray.map((item) => item.productId));
};

export type FlatShippingProductRow = {
    key: string;
    item: any;
    shippingNo: string;
};

export const flattenShippingListProducts = (
    shippingListItems: any[][],
    shippingListNo: string[],
    rowId: string
): FlatShippingProductRow[] => {
    const rows: FlatShippingProductRow[] = [];
    shippingListItems.forEach((products, index) => {
        products.forEach((item, itemIndex) => {
            rows.push({
                key: `product-${rowId}-${index}-${itemIndex}`,
                item,
                shippingNo: shippingListNo[index],
            });
        });
    });
    return rows;
};

const ShippingNumberItem: FC<{ shippingNo: string; showSeparator: boolean }> = ({ shippingNo, showSeparator }) => {
    const isLinkable = shippingNo && shippingNo !== '-';

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '12px' }}>
            {showSeparator && <span>,&nbsp;</span>}
            {isLinkable ? (
                <Link
                    className={coipCls('linktxt', COIP.inputLinkShippingNumber)}
                    to={{
                        pathname: `${other_link}/orderdetail?shippingListNo=${shippingNo}`,
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {shippingNo}
                </Link>
            ) : (
                <span>{shippingNo}</span>
            )}
        </Box>
    );
};

interface CustomerOrderProductTableProps {
    order: any;
    stockSize: Record<string, number> | undefined;
    onGoToProduct: (barcode: string) => void;
}

const CustomerOrderProductTable: FC<CustomerOrderProductTableProps> = ({ order, stockSize, onGoToProduct }) => {
    const { t } = useTranslation();
    const productRows = order.shippingListItems
        ? flattenShippingListProducts(order.shippingListItems, order.shippingListNo, order.rowId)
        : [];

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell align="center">{t('general.shippingNo')}</TableCell>
                    <TableCell align="center">{t('searchService.barcode')}</TableCell>
                    <TableCell align="center">{t('general.productId')}</TableCell>
                    <TableCell align="center">{t('general.stock')}</TableCell>
                    <TableCell align="center">{t('general.productDetail')}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {productRows.map(({ key, item, shippingNo }) => (
                    <TableRow key={key}>
                        <TableCell align="center">{shippingNo}</TableCell>
                        <TableCell align="center">{item.barcode}</TableCell>
                        <TableCell align="center">{item.productId}</TableCell>
                        <TableCell align="center">{stockSize?.[item.productId] ?? 0}</TableCell>
                        <TableCell align="center">
                            <p
                                className={coipCls('linktxt', COIP.inputLinkProductDetail)}
                                onClick={() => onGoToProduct(item.barcode)}
                            >
                                {t('customerInformation.clickProduct')}
                            </p>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export interface CustomerOrderRowProps {
    order: any;
    isDarkMode: boolean;
    canCreateRefund: boolean;
    onGoToProduct: (barcode: string) => void;
    onRowMenuClick: (popupState: any, action: string, order: any) => void;
}

export const CustomerOrderRow: FC<CustomerOrderRowProps> = ({
    order,
    isDarkMode,
    canCreateRefund,
    onGoToProduct,
    onRowMenuClick,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [innerOpen, setInnerOpen] = useState(false);
    const [stockSize, setStockSize] = useState<Record<string, number>>();

    const loadProductAvailableStock = async () => {
        const productIds = extractProductIdsFromShippingList(order.shippingListItems);
        if (!productIds.length) {
            return;
        }

        try {
            const res = await SearchServiceCommand.GetProductAvailableStock({ productIds });
            if (res) {
                setStockSize(res.data.productStockList);
                setInnerOpen(true);
            }
        } catch {
            Swal.fire(t('general.error'), ' ', 'error');
        }
    };

    const handleOpenClick = () => {
        const nextOpen = !open;
        setOpen(nextOpen);
        if (nextOpen) {
            setInnerOpen(true);
            loadProductAvailableStock();
        } else {
            setInnerOpen(false);
        }
    };

    const handleOrderStatusClick = () => {
        if (order.orderStatusId === 8 || order.orderStatusId === 22) {
            window.open('/returnList/?OrderNumber=' + order.orderNumber, '_blank');
        }
    };

    return (
        <>
            <TableRow sx={{ height: '60px !important' }}>
                <TableCell>
                    <IconButton
                        className={COIP.inputIconButtonExpandRow}
                        aria-label="expand row"
                        size="small"
                        onClick={handleOpenClick}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell align="center">
                    <b>
                        <Link
                            className={coipCls('linktxt', COIP.inputLinkOrderNumber)}
                            to={{
                                pathname: other_link + '/orderdetail?OrderNumber=' + order.orderNumber,
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {order.orderNumber}
                        </Link>
                    </b>
                </TableCell>
                <TableCell align="center">{TimeHelper.formatDateHour(order.orderDate)}</TableCell>
                <TableCell align="center">{formatOrderDeliveredDate(order.orderDeliveredDate)}</TableCell>
                <TableCell align="center">
                    <Box
                        className={COIP.fieldElapsedTimeBadge}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: order.passingTime >= 30 ? 'rgb(181, 70, 2)' : '#0d6efd',
                            padding: '5px',
                            color: 'white',
                            borderRadius: '5px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                        }}
                    >
                        {order.passingTime >= 30 ? '30+' : order.passingTime}
                    </Box>
                </TableCell>
                <TableCell align="center">{order.grandTotal}</TableCell>
                <TableCell
                    align="center"
                    sx={{
                        maxWidth: 110,
                        width: 110,
                        px: 0.5,
                    }}
                >
                    <Tooltip title={order.orderStatusName}>
                        <Chip
                            className={COIP.fieldChipOrderStatus}
                            size="small"
                            sx={{
                                maxWidth: '100%',
                                height: 'auto',
                                '& .MuiChip-label': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'block',
                                    fontSize: '13px',
                                    px: 0.5,
                                },
                            }}
                            style={{
                                backgroundColor: enumOrderStatus[order.orderStatusId]?.colorCode,
                                color: 'white',
                            }}
                            label={order.orderStatusName}
                            onClick={handleOrderStatusClick}
                        />
                    </Tooltip>
                </TableCell>
                <TableCell align="center">{order.paymentMethodName}</TableCell>
                <TableCell align="center">
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: 0.5,
                        }}
                    >
                        {order.shippingListNo.map((shippingNo: string, index: number) => (
                            <ShippingNumberItem
                                key={`${order.rowId}-shipping-${index}`}
                                shippingNo={shippingNo}
                                showSeparator={index > 0}
                            />
                        ))}
                    </Box>
                </TableCell>
                <TableCell align="center">{order.orderTypeDisplay}</TableCell>
                <TableCell
                    align="right"
                    sx={{
                        textAlign: 'center',
                    }}
                >
                    <PopupState variant="popover" popupId="demo-popup-menu">
                        {(popupState: any) => (
                            <>
                                <IconButton {...bindTrigger(popupState)} className={COIP.inputIconButtonRowMenu}>
                                    <MoreVert className="svg_icons" />{' '}
                                </IconButton>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem
                                        className={COIP.inputMenuItemGoToDetail}
                                        onClick={() => onRowMenuClick(popupState, 'gotoDetail', order)}
                                    >
                                        {t('general.goToDetail')}{' '}
                                    </MenuItem>
                                    {canCreateRefund && (
                                        <MenuItem
                                            className={COIP.inputMenuItemCreateRefund}
                                            onClick={() => onRowMenuClick(popupState, 'createReturn', order)}
                                        >
                                            {t('refund.createRefund')}{' '}
                                        </MenuItem>
                                    )}
                                </Menu>
                            </>
                        )}
                    </PopupState>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ padding: 0, backgroundColor: '#F3F3F3' }} colSpan={11}>
                    {innerOpen && (
                        <Box
                            className={COIP.nestedTableWrapper}
                            sx={{ width: '100%', backgroundColor: isDarkMode ? '#121212' : '#fff' }}
                        >
                            <CustomerOrderProductTable
                                order={order}
                                stockSize={stockSize}
                                onGoToProduct={onGoToProduct}
                            />
                        </Box>
                    )}
                </TableCell>
            </TableRow>
        </>
    );
};
